// src/client/game/scenes/ConfigScene.ts

import Phaser from "phaser";
import { initSfxEnabled, getSfxEnabled, setSfxEnabled } from "../audio/sfxSettings";

import {
  initMobileControlsMode,
  getMobileControlsMode,
  setMobileControlsMode,
  MobileControlsMode,
} from "../input/mobileControlsSettings";

type ConfigSceneData = {
  returnScene?: string; // optional: scene key to return to
};

/**
 * Speed setting (kept local to ConfigScene for now).
 * Stored in Phaser registry + localStorage.
 */
export type GameSpeedSetting = "low" | "med" | "high";

const SPEED_REGISTRY_KEY = "gameSpeedSetting";
const SPEED_LOCALSTORAGE_KEY = "laserSnake.gameSpeedSetting";

function initGameSpeedSetting(scene: Phaser.Scene): void {
  const existing = scene.registry.get(SPEED_REGISTRY_KEY) as GameSpeedSetting | undefined;
  if (existing === "low" || existing === "med" || existing === "high") return;

  let speed: GameSpeedSetting = "med";
  if (typeof window !== "undefined" && window.localStorage) {
    const stored = window.localStorage.getItem(SPEED_LOCALSTORAGE_KEY);
    if (stored === "low" || stored === "med" || stored === "high") speed = stored;
  }
  scene.registry.set(SPEED_REGISTRY_KEY, speed);
}

function getGameSpeedSetting(scene: Phaser.Scene): GameSpeedSetting {
  const val = scene.registry.get(SPEED_REGISTRY_KEY) as GameSpeedSetting | undefined;
  if (val === "low" || val === "med" || val === "high") return val;
  return "med";
}

function setGameSpeedSetting(scene: Phaser.Scene, speed: GameSpeedSetting): void {
  const s: GameSpeedSetting = speed === "low" || speed === "med" || speed === "high" ? speed : "med";
  scene.registry.set(SPEED_REGISTRY_KEY, s);
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.setItem(SPEED_LOCALSTORAGE_KEY, s);
  }
}

type SegOption<T extends string> = {
  value: T;
  label: string;
};

function makeCard(scene: Phaser.Scene, x: number, y: number, w: number, h: number) {
  const bg = scene.add.rectangle(x, y, w, h, 0x11111a, 0.92).setOrigin(0.5);
  bg.setStrokeStyle(1, 0xffffff, 0.16);
  return bg;
}

function makeLabel(scene: Phaser.Scene, x: number, y: number, text: string) {
  return scene.add
    .text(x, y, text, {
      fontFamily: "monospace",
      fontSize: "18px",
      color: "#ffffff",
    })
    .setOrigin(0, 0.5);
}

function makeSegmentedControl<T extends string>(args: {
  scene: Phaser.Scene;
  centerX: number;
  y: number;
  maxWidth: number; // available width inside the card
  options: Array<SegOption<T>>;
  getValue: () => T;
  setValue: (v: T) => void;
}) {
  const { scene, centerX, y, maxWidth, options, getValue, setValue } = args;

  const gap = 10;
  const minButtonW = 88;
  const buttonH = 42;

  // Compute button width from available space (mobile-first: never overflow).
  const rawW = Math.floor((maxWidth - gap * (options.length - 1)) / options.length);
  const buttonW = Math.max(minButtonW, rawW);

  // If we still don't fit (super narrow screens), shrink to fit.
  const totalW = buttonW * options.length + gap * (options.length - 1);
  const scale = totalW > maxWidth ? maxWidth / totalW : 1;

  const rects: Phaser.GameObjects.Rectangle[] = [];
  const texts: Phaser.GameObjects.Text[] = [];

  const startX = centerX - (totalW * scale) / 2 + (buttonW * scale) / 2;

  const setVisual = (r: Phaser.GameObjects.Rectangle, active: boolean, hover: boolean) => {
    const inactive = 0x141420;
    const hovered = 0x1f1f2d;
    const activeFill = 0x2a2a3a;

    if (active) r.setFillStyle(activeFill, 1);
    else if (hover) r.setFillStyle(hovered, 1);
    else r.setFillStyle(inactive, 1);

    r.setStrokeStyle(1, 0xffffff, active ? 0.35 : 0.16);
  };

  const refresh = (hoverRect?: Phaser.GameObjects.Rectangle) => {
    const current = getValue();
    rects.forEach((r, idx) => {
      const active = options[idx].value === current;
      const hover = hoverRect === r;
      setVisual(r, active, hover);
    });
  };

  options.forEach((opt, i) => {
    const bx = startX + i * (buttonW * scale + gap * scale);

    const r = scene.add
      .rectangle(bx, y, buttonW * scale, buttonH, 0x141420, 1)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    const t = scene.add
      .text(bx, y, opt.label, {
        fontFamily: "monospace",
        fontSize: "18px",
        color: "#ffffff",
      })
      .setOrigin(0.5);

    r.on("pointerup", () => {
      setValue(opt.value);
      refresh();
    });

    r.on("pointerover", () => refresh(r));
    r.on("pointerout", () => refresh());

    rects.push(r);
    texts.push(t);
  });

  refresh();
  return { refresh, rects, texts };
}

export class ConfigScene extends Phaser.Scene {
  private returnScene: string = "MenuScene";

  private titleText?: Phaser.GameObjects.Text;
  private backBtnBg?: Phaser.GameObjects.Rectangle;
  private backText?: Phaser.GameObjects.Text;

  constructor() {
    super("ConfigScene");
  }

  init(data: ConfigSceneData): void {
    if (data?.returnScene) this.returnScene = data.returnScene;
  }

  create(): void {
    initSfxEnabled(this);
    initMobileControlsMode(this);
    initGameSpeedSetting(this);

    const cam = this.cameras.main;
    const w = cam.width;
    const h = cam.height;
    const centerX = w / 2;

    this.cameras.main.setBackgroundColor(0x000000);

    this.titleText = this.add
      .text(centerX, 54, "Config", {
        fontFamily: "monospace",
        fontSize: "36px",
        color: "#ffffff",
      })
      .setOrigin(0.5, 0);

    // Mobile-first sizing
    const outerPad = 18;
    const cardW = Math.min(860, Math.max(300, w - outerPad * 2));
    const innerPad = 22;
    const contentW = cardW - innerPad * 2;

    const topPad = 120;
    const bottomPad = 130; // back button + hint
    const gap = 18;

    const available = Math.max(300, h - topPad - bottomPad);
    const cardH = Math.min(132, Math.max(108, Math.floor((available - gap * 2) / 3)));

    let y = topPad + cardH / 2;

    // --- Row 1: Sound Effects ---
    makeCard(this, centerX, y, cardW, cardH);
    makeLabel(this, centerX - cardW / 2 + innerPad, y - cardH / 2 + 28, "🔊  Sound Effects");

    const sfxControl = makeSegmentedControl<"on" | "off">({
      scene: this,
      centerX,
      y: y + 18,
      maxWidth: contentW,
      options: [
        { value: "on", label: "ON" },
        { value: "off", label: "OFF" },
      ],
      getValue: () => (getSfxEnabled(this) ? "on" : "off"),
      setValue: (v) => setSfxEnabled(this, v === "on"),
    });

    // --- Row 2: Mobile Controls ---
    y += cardH + gap;
    makeCard(this, centerX, y, cardW, cardH);
    makeLabel(this, centerX - cardW / 2 + innerPad, y - cardH / 2 + 28, "📱  Mobile Controls");

    const controlsControl = makeSegmentedControl<MobileControlsMode>({
      scene: this,
      centerX,
      y: y + 18,
      maxWidth: contentW,
      options: [
        { value: "dpad", label: "D-PAD" },
        { value: "joystick", label: "JOYSTK" },
        { value: "swipe", label: "SWIPE" },
      ],
      getValue: () => getMobileControlsMode(this),
      setValue: (v) => setMobileControlsMode(this, v),
    });

    // --- Row 3: Speed ---
    y += cardH + gap;
    makeCard(this, centerX, y, cardW, cardH);
    makeLabel(this, centerX - cardW / 2 + innerPad, y - cardH / 2 + 28, "🐢🐇  Speed");

    const speedControl = makeSegmentedControl<GameSpeedSetting>({
      scene: this,
      centerX,
      y: y + 18,
      maxWidth: contentW,
      options: [
        { value: "low", label: "🐢 LOW" },
        { value: "med", label: "MED" },
        { value: "high", label: "🐇 HIGH" },
      ],
      getValue: () => getGameSpeedSetting(this),
      setValue: (v) => setGameSpeedSetting(this, v),
    });

    // --- BACK button ---
    const backY = h - 80;

    this.backBtnBg = this.add
      .rectangle(centerX, backY, Math.min(360, cardW * 0.65), 54, 0x1a1a24, 1)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.backBtnBg.setStrokeStyle(1, 0xffffff, 0.18);

    this.backText = this.add
      .text(centerX, backY, "BACK", {
        fontFamily: "monospace",
        fontSize: "20px",
        color: "#ffffff",
      })
      .setOrigin(0.5);

    this.backBtnBg.on("pointerover", () => this.backBtnBg?.setFillStyle(0x232332, 1));
    this.backBtnBg.on("pointerout", () => this.backBtnBg?.setFillStyle(0x1a1a24, 1));
    this.backBtnBg.on("pointerup", () => this.goBack());

    this.input.keyboard?.on("keydown-ESC", () => this.goBack());

    this.add
      .text(centerX, h - 36, "ESC to go back", {
        fontFamily: "monospace",
        fontSize: "14px",
        color: "#8f8f9e",
      })
      .setOrigin(0.5);

    // Keep visuals correct if the scene is revisited.
    this.events.on("wake", () => {
      sfxControl.refresh();
      controlsControl.refresh();
      speedControl.refresh();
    });
  }

  private goBack(): void {
    const keys = (this.scene.manager as any).keys as Record<string, unknown> | undefined;

    const tryStart = (key: string): boolean => {
      if (keys && keys[key]) {
        this.scene.start(key);
        return true;
      }
      return false;
    };

    if (tryStart(this.returnScene)) return;
    if (tryStart("MenuScene")) return;
    if (tryStart("MainMenu")) return;

    this.scene.stop();
  }
}
